import { saveQueueService } from './save-queue.service';
import { ContentService } from './api';
import { useEditorStore } from '@/stores/editor.store';
import { activityTracker, EventType, EngagementEvent } from './activity-tracker';

/**
 */
class BackgroundSaveService {
  private isProcessing = false;
  private processingInterval: NodeJS.Timeout | null = null;
  private readonly PROCESS_INTERVAL = 3000;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAYS = [1000, 3000, 10000]; // Exponential backoff

  /**
   */
  start(): void {
    console.log('🚀 BackgroundSaveService started');

    this.processingInterval = setInterval(() => {
      this.processQueue();
    }, this.PROCESS_INTERVAL);

    window.addEventListener('save-queue:process', () => {
      this.processQueue();
    });

    this.processQueue();
  }

  /**
   */
  stop(): void {
    console.log('🛑 BackgroundSaveService stopped');

    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }
  }

  /**
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing) {
      console.log('⏸️ Already processing...');
      return;
    }

    this.isProcessing = true;

    try {
      const pendingItems = saveQueueService.getPendingItems();

      if (pendingItems.length === 0) {
        return;
      }

      console.log(`🔄 Items to process: ${pendingItems.length}`);

      const chunks = this.chunkArray(pendingItems, 3);

      for (const chunk of chunks) {
        await Promise.allSettled(
          chunk.map(item => this.processItem(item))
        );
      }
    } catch (error) {
      console.error('Queue processing error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   */
  private async processItem(item: {
    topicNodeId: string;
    segments: Array<{ segmentKey: string; content: string; order: number; isCustom?: boolean; isDeleted?: boolean }>;
    retryCount: number;
  }): Promise<void> {
    const { topicNodeId, segments, retryCount } = item;

    useEditorStore.getState().setSaveStatus(topicNodeId, 'saving');

    try {
      console.log(`💾 Save attempt: ${topicNodeId} (attempt ${retryCount + 1})`);

      await ContentService.updateUserContentSegments(topicNodeId, segments);

      console.log(`✅ Save succeeded: ${topicNodeId}`);

      await saveQueueService.dequeue(topicNodeId);

      useEditorStore.getState().setSaveStatus(topicNodeId, 'saved');

      setTimeout(() => {
        const currentStatus = useEditorStore.getState().editorStates[topicNodeId]?.saveStatus;
        if (currentStatus === 'saved') {
          useEditorStore.getState().setSaveStatus(topicNodeId, 'idle');
        }
      }, 2000);

      activityTracker.track(EventType.ENGAGEMENT, EngagementEvent.CONTENT_EDITED, {
        entityType: 'node',
        entityId: topicNodeId,
        properties: {
          segmentCount: segments.length
        }
      });

      window.dispatchEvent(
        new CustomEvent('save-queue:success', {
          detail: { topicNodeId },
        })
      );
    } catch (error) {
      console.error(`❌ Save failed: ${topicNodeId}`, error);

      const errorMessage = (error as Error).message;

      if (retryCount < this.MAX_RETRIES) {
        await saveQueueService.incrementRetry(topicNodeId, errorMessage);

        const delay = this.RETRY_DELAYS[retryCount] || 10000;
        console.log(`🔄 Retrying after ${delay}ms...`);

        setTimeout(() => {
          this.processQueue();
        }, delay);

        useEditorStore.getState().setSaveStatus(topicNodeId, 'idle');
      } else {
        console.error(`🚫 Maximum retries exceeded: ${topicNodeId}`);

        useEditorStore.getState().setError(
          topicNodeId,
          new Error(`Save failed: ${errorMessage}`)
        );

        window.dispatchEvent(
          new CustomEvent('save-queue:failed', {
            detail: { topicNodeId, error: errorMessage },
          })
        );
      }
    }
  }

  /**
   */
  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }
}

export const backgroundSaveService = new BackgroundSaveService();
